/**
 * MSR Portal Phase 2 — Sales History (read-only).
 *
 * The logged-in rep's own attributed signups (voucher redemptions + CTA-attributed
 * registrations) across their events, with Zuora-verified status. All data is scoped
 * server-side to the rep's sales positions by get_msr_sales_history / _summary — a
 * non-rep sees nothing. Mobile-first, mirrors the other MSR portal pages.
 */
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getMySaleTotals } from "../../services/msrFeedback.service";
import MsrTabs from "../../components/msr/MsrTabs";
import {
  getMsrSalesHistory,
  getMsrSalesHistorySummary,
} from "../../services/msrPortal.service";
import { EVENT_TYPES } from "../../utils/constants";
import { useAuth } from "../../contexts/AuthContext";
import {
  clampMsrFrom,
  msrHistoryFloor,
} from "../../utils/msrSalesHistoryWindow";

const PAGE_SIZE = 50;

function fmtDate(s: string | null): string {
  if (!s) {
    return "—";
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

/**
 * The event's date as the filter sees it: its LOCAL calendar date in its own timezone (migration 060). Formatting
 * the raw start instant in the device's zone could show a different day near midnight than the one the From/To
 * filters matched (round-2 audit MSR2-M2). A bare YYYY-MM-DD is formatted at UTC noon so no zone can shift it.
 */
export function fmtEventLocalDate(
  localDate: string | null | undefined,
  fallbackInstant: string | null,
): string {
  if (localDate && /^\d{4}-\d{2}-\d{2}$/.test(localDate)) {
    return new Date(`${localDate}T12:00:00Z`).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  }
  return fmtDate(fallbackInstant);
}

const MsrSalesHistory: React.FC = () => {
  const { user } = useAuth();
  // MSR plan slice 1 (MSR-03/04): an MSR sees the last 90 days by event date and has no event-type filter.
  // The server enforces both for role msr; admins sharing this page keep the unbounded view.
  const isMsr = (user?.role ?? "").toLowerCase() === "msr";
  const floor = isMsr ? msrHistoryFloor() : undefined;
  const [dateFrom, setDateFrom] = useState(floor ?? "");
  const [dateTo, setDateTo] = useState("");
  const [eventType, setEventType] = useState("all");
  const [page, setPage] = useState(1);

  const filter = {
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    eventType: eventType === "all" ? undefined : eventType,
  };

  const { data: summary } = useQuery({
    queryKey: ["msr-sales-history-summary", filter],
    queryFn: () => getMsrSalesHistorySummary(filter),
  });

  // Business lead 2026-10-27: sales verified by email, and the reinstatements/upgrades the rep typed in (separate,
  // not part of close rate). Same date window; the server applies the 90-day floor for MSRs.
  const { data: totals } = useQuery({
    queryKey: [
      "msr-sale-totals",
      filter.dateFrom ?? null,
      filter.dateTo ?? null,
    ],
    queryFn: () =>
      getMySaleTotals(filter.dateFrom ?? null, filter.dateTo ?? null),
    retry: false,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["msr-sales-history", filter, page],
    queryFn: () => getMsrSalesHistory({ ...filter, page, limit: PAGE_SIZE }),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Reset to page 1 whenever a filter changes.
  const onFilterChange = (fn: () => void) => {
    fn();
    setPage(1);
  };

  const tiles = [
    { label: "Signups", value: summary?.total_signups ?? 0 },
    // Owner R2: two different "verified" numbers, labelled apart. This one: QR/voucher sign-ups with a subscription found.
    {
      label: "Sign-ups with a subscription",
      value: summary?.verified_count ?? 0,
    },
    { label: "Pending", value: summary?.pending_count ?? 0 },
    { label: "Events", value: summary?.event_count ?? 0 },
  ];

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-4">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold text-gray-900">Sales History</h1>
        <p className="text-sm text-gray-600">
          Your attributed sign-ups across your events. "Sign-ups with a
          subscription" counts QR/voucher sign-ups whose membership was found.
          "Sales you verified by email" counts sales you verified on the Verify
          a Sale tab.
        </p>
      </header>

      <MsrTabs active="sales-history" />

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {tiles.map((t) => (
          <div
            key={t.label}
            className="rounded-lg border border-gray-200 bg-white p-3 text-center"
          >
            <div className="text-2xl font-bold text-gray-900">{t.value}</div>
            <div className="text-xs text-gray-500">{t.label}</div>
          </div>
        ))}
      </div>

      {totals && (
        <div className="grid grid-cols-3 gap-2" data-testid="msr-sale-totals">
          {[
            {
              label: "Sales you verified by email",
              value: totals.verified_sales,
            },
            {
              label: "Reinstatements (typed in)",
              value: totals.reinstatements,
            },
            { label: "Upgrades (typed in)", value: totals.upgrades },
          ].map((t) => (
            <div
              key={t.label}
              className="rounded-lg border border-green-200 bg-green-50 p-3 text-center"
            >
              <div className="text-2xl font-bold text-gray-900">{t.value}</div>
              <div className="text-xs text-gray-600">{t.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
        <div className="flex flex-col">
          <label className="text-xs text-gray-600 mb-1">From</label>
          <input
            type="date"
            value={dateFrom}
            min={floor}
            onChange={(e) =>
              onFilterChange(() =>
                setDateFrom(
                  floor ? clampMsrFrom(e.target.value, floor) : e.target.value,
                ),
              )
            }
            className="px-2 py-1 border border-gray-300 rounded text-sm"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-gray-600 mb-1">To</label>
          <input
            type="date"
            value={dateTo}
            min={dateFrom || floor}
            onChange={(e) => onFilterChange(() => setDateTo(e.target.value))}
            className="px-2 py-1 border border-gray-300 rounded text-sm"
          />
        </div>
        {!isMsr && (
          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Event type</label>
            <select
              value={eventType}
              onChange={(e) =>
                onFilterChange(() => setEventType(e.target.value))
              }
              className="px-2 py-1 border border-gray-300 rounded text-sm bg-white"
            >
              <option value="all">All types</option>
              {EVENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        )}
        {floor && (
          <p className="text-xs text-gray-500 self-center">
            Showing events from the last 90 days.
          </p>
        )}
        {((dateFrom && dateFrom !== floor) ||
          dateTo ||
          eventType !== "all") && (
          <button
            onClick={() =>
              onFilterChange(() => {
                setDateFrom(floor ?? "");
                setDateTo("");
                setEventType("all");
              })
            }
            className="text-sm text-primary-600 hover:text-primary-800"
          >
            Clear
          </button>
        )}
      </div>

      {isLoading && (
        <div className="text-center text-gray-500 py-12">Loading…</div>
      )}

      {error !== null && error !== undefined && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-900">
          {(error as Error).message}
        </div>
      )}

      {!isLoading && rows.length === 0 && (
        <div className="text-center text-gray-600 py-12 px-4">
          <p className="font-medium text-gray-900">No signups found.</p>
          <p className="mt-2 text-sm">
            Signups attributed to your CTAs will appear here.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {rows.map((r) => {
          const name =
            [r.contact_first_name, r.contact_last_name]
              .filter(Boolean)
              .join(" ") || "(no name)";
          return (
            <div
              key={`${r.source_type}:${r.voucher_id ?? r.cta_id ?? ""}:${r.contact_id}:${r.event_id}:${r.signup_at ?? ""}`}
              className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-2">
                <h2 className="font-semibold text-gray-900">{name}</h2>
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded ${
                    r.is_zuora_confirmed
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {r.is_zuora_confirmed ? "Verified" : "Pending"}
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-700">
                {r.event_title ?? "Untitled event"}
                {r.event_date || r.event_local_date
                  ? ` · ${fmtEventLocalDate(r.event_local_date, r.event_date)}`
                  : ""}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 capitalize">
                  {r.source_type === "voucher" ? "Voucher" : "Registration"}
                </span>
                {r.cta_name && (
                  <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                    {r.cta_name}
                  </span>
                )}
                {r.member_number && <span>Member #{r.member_number}</span>}
                {r.signup_at && <span>Signed up {fmtDate(r.signup_at)}</span>}
              </div>
              {(r.contact_email || r.contact_phone) && (
                <p className="mt-1 text-xs text-gray-500">
                  {[r.contact_email, r.contact_phone]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between pt-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="text-sm px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-xs text-gray-500">
            Page {page} of {totalPages} · {total} total
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="text-sm px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default MsrSalesHistory;
